import { create } from "zustand";
import {
    ExplorerEntryType,
    IExplorerResolvedKeys,
} from "../declarations/IExplorerTypes";

interface IExplorerStore {
    entryType: ExplorerEntryType;
    orderID: number;
    selectedNodeId: string | null;
    resolvedKeys: IExplorerResolvedKeys | null;
    expandedLineNumbers: number[];
    /** Composite line keys for service-order line expansion (lineNumber alone is not unique because
     * a single CS service-order line can map to multiple D365 line "jobs"). */
    expandedServiceLineKeys: string[];
    /** Group-level expansion for service-order lines (group key = externalItemNumber). */
    expandedServiceLineGroups: string[];
    expandedReleasedProducts: string[];
    expandedOperations: string[];
    expandedBomLines: string[];

    setEntry: (entryType: ExplorerEntryType, orderID: number) => void;
    setSelectedNodeId: (nodeId: string | null) => void;
    setResolvedKeys: (keys: IExplorerResolvedKeys) => void;
    toggleLineExpanded: (lineNumber: number) => void;
    toggleServiceLineExpanded: (lineKey: string) => void;
    toggleServiceLineGroupExpanded: (groupKey: string) => void;
    toggleReleasedProductExpanded: (rpKey: string) => void;
    toggleOperationExpanded: (opKey: string) => void;
    toggleBomLineExpanded: (bomLineKey: string) => void;
    reset: () => void;
}

export const useExplorerStore = create<IExplorerStore>((set, get) => ({
    entryType: "SalesOrder",
    orderID: 0,
    selectedNodeId: null,
    resolvedKeys: null,
    expandedLineNumbers: [],
    expandedServiceLineKeys: [],
    expandedServiceLineGroups: [],
    expandedReleasedProducts: [],
    expandedOperations: [],
    expandedBomLines: [],

    setEntry: (entryType, orderID) =>
        set({
            entryType,
            orderID,
            selectedNodeId: null,
            resolvedKeys: null,
            expandedLineNumbers: [],
            expandedServiceLineKeys: [],
            expandedServiceLineGroups: [],
            expandedReleasedProducts: [],
            expandedOperations: [],
            expandedBomLines: [],
        }),

    setSelectedNodeId: (nodeId) => set({ selectedNodeId: nodeId }),

    setResolvedKeys: (keys) => set({ resolvedKeys: keys }),

    toggleLineExpanded: (lineNumber) => {
        const current = get().expandedLineNumbers;
        const isExpanded = current.includes(lineNumber);
        set({
            expandedLineNumbers: isExpanded
                ? current.filter((n) => n !== lineNumber)
                : [...current, lineNumber],
        });
    },

    toggleServiceLineExpanded: (lineKey) => {
        const current = get().expandedServiceLineKeys;
        const isExpanded = current.includes(lineKey);
        set({
            expandedServiceLineKeys: isExpanded
                ? current.filter((k) => k !== lineKey)
                : [...current, lineKey],
        });
    },

    toggleServiceLineGroupExpanded: (groupKey) => {
        const current = get().expandedServiceLineGroups;
        const isExpanded = current.includes(groupKey);
        set({
            expandedServiceLineGroups: isExpanded
                ? current.filter((k) => k !== groupKey)
                : [...current, groupKey],
        });
    },

    toggleReleasedProductExpanded: (rpKey) => {
        const current = get().expandedReleasedProducts;
        const isExpanded = current.includes(rpKey);
        set({
            expandedReleasedProducts: isExpanded
                ? current.filter((k) => k !== rpKey)
                : [...current, rpKey],
        });
    },

    toggleOperationExpanded: (opKey) => {
        const current = get().expandedOperations;
        const isExpanded = current.includes(opKey);
        set({
            expandedOperations: isExpanded
                ? current.filter((k) => k !== opKey)
                : [...current, opKey],
        });
    },

    toggleBomLineExpanded: (bomLineKey) => {
        const current = get().expandedBomLines;
        const isExpanded = current.includes(bomLineKey);
        set({
            expandedBomLines: isExpanded
                ? current.filter((k) => k !== bomLineKey)
                : [...current, bomLineKey],
        });
    },

    reset: () =>
        set({
            entryType: "SalesOrder",
            orderID: 0,
            selectedNodeId: null,
            resolvedKeys: null,
            expandedLineNumbers: [],
            expandedServiceLineKeys: [],
            expandedServiceLineGroups: [],
            expandedReleasedProducts: [],
            expandedOperations: [],
            expandedBomLines: [],
        }),
}));
